// English is the master/source language for audience question/option text.
// It is always offered as a display option so the canonical text stays viewable
// even for a market that doesn't allocate it.
const MASTER_LANGUAGE = 'en'

export interface MarketLanguageAllocation {
  language: string
  percent: number
}

export interface MarketLanguages {
  // Display options for the language switch: the market's languages (primary
  // first) plus the English master, de-duplicated case-insensitively.
  languages: string[]
  // The market's primary language: the dominant (highest-percent) allocation,
  // falling back to the locale language (single-language / legacy markets).
  primary: string | undefined
  // True when the market genuinely has more than one allocated language, i.e.
  // when a language switch is worth showing. Single-language markets keep the
  // default (primary only) with no switch.
  isMultiLanguage: boolean
}

const pushUnique = (list: string[], language: string | null | undefined) => {
  if (
    language &&
    !list.some((l) => l.toLowerCase() === language.toLowerCase())
  ) {
    list.push(language)
  }
}

/**
 * Derives the market's display languages from its fieldwork language
 * allocations. The dominant allocation is the primary language; allocations are
 * ordered dominant-first, then the locale language and the English master are
 * appended as options. Pure so it can be unit-tested without Apollo.
 */
export const deriveMarketLanguages = (
  allocations: MarketLanguageAllocation[] | null | undefined,
  localeLanguage: string | null | undefined
): MarketLanguages => {
  // Dominant (highest-percent) allocation first. Array.sort is stable, so
  // equal-percent languages keep their backend order.
  const sorted = [...(allocations ?? [])].sort((a, b) => b.percent - a.percent)

  const allocated: string[] = []
  sorted.forEach((allocation) => pushUnique(allocated, allocation.language))

  // The dominant allocation is the primary language; with no allocations, fall
  // back to the locale language (single-language / legacy markets).
  let primary: string | undefined = localeLanguage ?? undefined
  if (allocated.length > 0) {
    primary = allocated[0]
  }
  const isMultiLanguage = allocated.length >= 2

  const languages = [...allocated]
  // A single-language / legacy market has no allocations — surface its locale
  // language so it is still selectable.
  pushUnique(languages, localeLanguage)
  pushUnique(languages, MASTER_LANGUAGE)

  return { languages, primary, isMultiLanguage }
}
